import {
  BoxesIcon,
  CalendarClockIcon,
  ClipboardListIcon,
  FactoryIcon,
  LayoutDashboardIcon,
  PackageSearchIcon,
  ShapesIcon,
  WalletIcon,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useRouterState } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { canAccessAppPath, type Role } from '../../worker/api/auth.contract'

/** Every route the SPA serves. Kept as a union so <Link to> stays type-checked. */
export type AppPath =
  | '/'
  | '/stok'
  | '/siparisler'
  | '/satinalma'
  | '/cari'
  | '/finans'
  | '/uretim'
  | '/modeller'

export interface NavItem {
  to: AppPath
  /** Sidebar + topbar label. */
  label: string
  icon: LucideIcon
}

export const NAV: readonly NavItem[] = [
  { to: '/', label: 'Panel', icon: LayoutDashboardIcon },
  { to: '/stok', label: 'Stok', icon: BoxesIcon },
  { to: '/siparisler', label: 'Siparişler', icon: ClipboardListIcon },
  { to: '/satinalma', label: 'Satınalma', icon: PackageSearchIcon },
  { to: '/cari', label: 'Cari', icon: WalletIcon },
  { to: '/finans', label: 'Finans', icon: CalendarClockIcon },
  { to: '/uretim', label: 'Üretim', icon: FactoryIcon },
  { to: '/modeller', label: 'Modeller', icon: ShapesIcon },
]

/**
 * The sidebar a given role gets: every entry whose module the Worker would
 * answer with a 403 drops out. `staff` loses Cari, Satınalma and Finans,
 * `purchasing` keeps Satınalma alone of the three, `admin` keeps everything —
 * but none of that is written here. It is asked per entry against `GUARDED_AREAS` in the auth contract, the
 * same table the Worker's gate reads, so a module can never be hidden from a
 * role that may open it (or, worse, offered to one that may not).
 *
 * Hiding is courtesy, not security: the enforcement is server-side, and the
 * route guards in router.tsx catch a typed-in address. What this prevents is a
 * sidebar full of entries that only ever lead to an error card.
 */
export function navForRole(role: Role): readonly NavItem[] {
  return NAV.filter((item) => canAccessAppPath(role, item.to))
}

/**
 * Longest-prefix match, so nested routes keep their parent nav item active.
 *
 * Deliberately over the FULL `NAV`, not the role's subset: this also names the
 * topbar heading, and a user who lands on a module closed to them for the
 * moment before the guard bounces them should see the page's own title, not
 * "Panel".
 */
export function activeNavItem(pathname: string): NavItem | undefined {
  return NAV.filter((item) => item.to === '/' ? pathname === '/' : pathname.startsWith(item.to)).at(
    -1,
  )
}

/* ── List-screen URL state ──────────────────────────────────────────────────
 * The list screens keep their search text in the address bar (see the
 * `validateSearch` schemas in router.tsx). Typing straight into the URL would
 * push a history entry per keystroke and refetch on every letter, so the box
 * keeps its own state and settles into the URL after a pause. The sync is
 * subtle enough that every list screen shares this one implementation.
 */

/** Keystroke pause before the search box writes itself into the URL. */
const SEARCH_DEBOUNCE_MS = 300

/**
 * Longest search text the hook commits AND router.tsx keeps from the URL
 * (defined here because router imports the screens, which import this file —
 * the other direction would be a cycle). The APIs truncate at 100 too. One
 * shared cap: if the hook committed more than the URL keeps, `settled` and
 * the address bar would disagree forever about the invisible tail.
 */
export const MAX_QUERY_LENGTH = 100

/**
 * Two-way binding between a text input and a URL search param.
 *
 * Returns the live input value, its setter, and `flush` — see below. `commit`
 * is called with the trimmed text once typing stops; it should write the
 * param **replace-style**, so Back leaves the screen instead of walking back
 * one character at a time.
 *
 * The URL is also allowed to move on its own — Back/Forward, "Filtreleri
 * temizle", a supplier tile writing the code into the box — and the input
 * follows it. `settled` remembers the last value the two agreed on, which is
 * what tells an outside change apart from our own write echoing back.
 */
export function useUrlSearchInput(
  urlValue: string,
  commit: (value: string) => void,
  delay: number = SEARCH_DEBOUNCE_MS,
): [string, (value: string) => void, (opts?: { commit?: boolean }) => string] {
  const [value, setValue] = useState(urlValue)
  const settled = useRef(urlValue)
  const commitRef = useRef(commit)
  /*
   * ANY route-location change — not just a change of the query param itself —
   * must drop a pending debounce commit. Back/Forward between two entries that
   * share the same query but differ in another filter would otherwise leave the
   * timer alive, and 300ms later it would replace the restored entry with the
   * uncommitted text. The URL is the single writer; external navigation resets
   * the box to whatever the restored URL says.
   */
  // Hash included: board anchor links (#emir-listesi) push entries without
  // touching the search string, and "any location change" must mean the hash
  // too or a timer survives the detour and fires against the wrong entry.
  const locationKey = useRouterState({ select: (s) => `${s.location.searchStr}#${s.location.hash}` })
  const lastLocationKey = useRef(locationKey)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const cancelPending = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }

  useEffect(() => {
    commitRef.current = commit
  })

  useEffect(() => {
    if (locationKey === lastLocationKey.current) return
    lastLocationKey.current = locationKey
    // The timer must die even when setValue below is a no-op (restored query
    // equal to the typed text): a surviving commit would replace the restored
    // entry — e.g. wiping its page — 300ms after the navigation.
    cancelPending()
    settled.current = urlValue
    setValue(urlValue)
  }, [locationKey, urlValue])

  useEffect(() => {
    if (urlValue === settled.current) return
    settled.current = urlValue
    setValue(urlValue)
  }, [urlValue])

  useEffect(() => {
    const next = value.trim().slice(0, MAX_QUERY_LENGTH)
    if (next === settled.current) return
    cancelPending()
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      settled.current = next
      commitRef.current(next)
    }, delay)
    return cancelPending
  }, [value, delay])

  /*
   * A click that raced the debounce used to LOSE the text: mousedown on a
   * filter chip or the export button inside the 300 ms window, the filter's
   * navigation cancelled the pending commit, and the box silently reset.
   * `flush` hands the interaction the text instead. Plain `flush()` commits
   * right now (for actions that do not navigate, like the CSV export — the
   * screen then catches up to the exported text). `flush({ commit: false })`
   * only stops the timer and returns the text, for a filter write to carry in
   * its OWN navigation — two back-to-back navigations would race on `prev`,
   * so whoever navigates must stay the single writer. `settled` is left
   * untouched in that mode: the caller's navigation echoes back through the
   * location effect and settles it, and if the caller drops the text instead,
   * the box visibly resets rather than diverging from the URL forever.
   */
  const flush = (opts?: { commit?: boolean }): string => {
    cancelPending()
    const next = value.trim().slice(0, MAX_QUERY_LENGTH)
    if (next === settled.current || opts?.commit === false) return next
    settled.current = next
    commitRef.current(next)
    return next
  }

  return [value, setValue, flush]
}
